/**
 * Bandeaux d’état persistants (lecture seule licence, etc.).
 * Remplace un toast éphémère pour les situations qui durent toute la session.
 */

import {
  CLOUD_LICENCE_REFUSAL_EVENT,
  cloudLicenceMessage,
} from '@application/licence/CloudLicenceMessages';
import { LICENCE_UPDATED_EVENT } from '@application/licence/LicenceEntitlementService';

export const READ_ONLY_BANNER_ID = 'nexxus-readonly-banner';

export const READ_ONLY_BANNER_MESSAGE =
  'Licence en lecture seule : consultation et export possibles, saisie bloquée.';

/** Affiche (ou met à jour) le bandeau lecture seule sous le topbar / dans main. */
export function showReadOnlyBanner(root: ParentNode, message: string = READ_ONLY_BANNER_MESSAGE): void {
  const host =
    root.querySelector<HTMLElement>('main.main') ??
    (root instanceof HTMLElement ? root : null);
  if (!host) return;

  let banner = host.querySelector<HTMLElement>(`#${READ_ONLY_BANNER_ID}`);
  if (!banner) {
    banner = document.createElement('div');
    banner.id = READ_ONLY_BANNER_ID;
    banner.className = 'persistent-banner persistent-banner--readonly';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    host.prepend(banner);
  }
  banner.hidden = false;
  banner.textContent = message;
}

export function hideReadOnlyBanner(root: ParentNode): void {
  root.querySelector<HTMLElement>(`#${READ_ONLY_BANNER_ID}`)?.remove();
}

/** Synchronise le bandeau avec l’état runtime (lecture seule / restriction). */
export function syncReadOnlyBanner(
  root: ParentNode,
  opts: { isReadOnly: boolean; message?: string },
): void {
  if (opts.isReadOnly) {
    showReadOnlyBanner(root, opts.message ?? READ_ONLY_BANNER_MESSAGE);
  } else {
    hideReadOnlyBanner(root);
  }
}

/** Surface du runtime licence lue par le bandeau (permet de le tester sans runtime complet). */
export interface ReadOnlyBannerRuntime {
  getState(): { isReadOnly: boolean; errorCode?: string; claims?: { exp: number } | undefined };
  getRestriction(): { message: string } | null;
}

/**
 * État réel du bandeau : restriction serveur (message propre) > expiration > lecture seule générique.
 * Ne masque jamais un blocage : toute lecture seule du runtime (ou restriction) affiche le bandeau.
 */
export function licenceReadOnlyBannerState(
  runtime: ReadOnlyBannerRuntime | null | undefined,
  nowSec: number = Math.floor(Date.now() / 1000),
): { isReadOnly: boolean; message?: string } {
  if (!runtime) return { isReadOnly: false };
  const state = runtime.getState();
  const restriction = runtime.getRestriction();
  const expired =
    state.errorCode === 'EXPIRED' || Boolean(state.claims && state.claims.exp <= nowSec);
  const isReadOnly = Boolean(state.isReadOnly || expired || restriction);
  if (!isReadOnly) return { isReadOnly: false };
  if (restriction) return { isReadOnly: true, message: restriction.message };
  if (expired) return { isReadOnly: true, message: cloudLicenceMessage('license_expired') ?? READ_ONLY_BANNER_MESSAGE };
  return { isReadOnly: true, message: READ_ONLY_BANNER_MESSAGE };
}

export interface ReadOnlyBannerBinding {
  refresh(): void;
  dispose(): void;
}

/**
 * Relie le bandeau à l’état licence : rafraîchi à chaque mise à jour de licence et à chaque
 * refus de licence reçu en cours de session (sans attendre le contrôle périodique).
 */
export function bindLicenceReadOnlyBanner(
  root: ParentNode,
  getRuntime: () => ReadOnlyBannerRuntime | null | undefined,
): ReadOnlyBannerBinding {
  const refresh = (): void => {
    syncReadOnlyBanner(root, licenceReadOnlyBannerState(getRuntime()));
  };
  window.addEventListener(LICENCE_UPDATED_EVENT, refresh);
  window.addEventListener(CLOUD_LICENCE_REFUSAL_EVENT, refresh);
  return {
    refresh,
    dispose: () => {
      window.removeEventListener(LICENCE_UPDATED_EVENT, refresh);
      window.removeEventListener(CLOUD_LICENCE_REFUSAL_EVENT, refresh);
    },
  };
}
